import { menuSections, type MenuItem } from '@/config/menu-config';
import { getSectionPermissions } from '@/config/section-access';

export type HomeDestination =
  | { kind: 'section'; id: string }
  | { kind: 'artemis'; id: 'artemis' }
  | { kind: 'batcave'; id: 'batcave' };

export interface HomeNavigationCard {
  id: string;
  title: string;
  description: string;
  icon: MenuItem['icon'];
  destination: HomeDestination;
}

export interface HomeNavigationSection {
  id: 'workspace' | 'production' | 'tools';
  title: string;
  description: string;
  cards: HomeNavigationCard[];
}

export interface HomeReportLink {
  id: string;
  title: string;
  destination: Extract<HomeDestination, { kind: 'section' }>;
}

export interface HomeNavigationModel {
  sections: HomeNavigationSection[];
  reports: HomeReportLink[];
}

export type HomeWidgetSectionId = Extract<
  HomeNavigationSection['id'],
  'workspace' | 'production'
>;

export interface HomeWithheldWidget {
  id: string;
  title: string;
  icon: MenuItem['icon'];
}

type HasAnyPermission = (permissions: string[]) => boolean;

interface HomeCardPresentation {
  menuItemId: string;
  description: string;
}

interface HomeSectionPresentation {
  id: HomeNavigationSection['id'];
  title: string;
  description: string;
  cards: readonly HomeCardPresentation[];
}

const HOME_SECTION_PRESENTATION: readonly HomeSectionPresentation[] = [
  {
    id: 'workspace',
    title: 'Workspace',
    description: 'Oversight, people and the company itself.',
    cards: [
      { menuItemId: 'system-status', description: 'System and integration health.' },
      { menuItemId: 'company-settings', description: 'Company setup, access and integrations.' },
      { menuItemId: 'talent-pipeline', description: 'Recruiting prospects and workflows.' },
      { menuItemId: 'batcave', description: 'Archived documents.' }
    ]
  },
  {
    id: 'production',
    title: 'Production',
    description: 'Loans moving and people to call.',
    cards: [
      { menuItemId: 'pipelines', description: 'Disclosure tracking.' },
      { menuItemId: 'loan-center', description: 'Live loans and post-closing workflows.' },
      { menuItemId: 'income-calculation', description: 'Borrower income worksheets.' },
      { menuItemId: 'borrowers', description: 'Borrower and lead relationships.' },
      { menuItemId: 'referral-partners', description: 'Referral partner relationships.' },
      { menuItemId: 'manage-investors', description: 'Investor roster.' }
    ]
  },
  {
    id: 'tools',
    title: 'Tools',
    description: 'One-click doors.',
    cards: [
      { menuItemId: 'artemis', description: 'rate hunting console' },
      { menuItemId: 'pre-approval', description: 'Create and manage pre-approvals.' },
      { menuItemId: 'ai-support', description: 'ask anything' },
      { menuItemId: 'ai-settings', description: 'Configure AI tools.' },
      { menuItemId: 'accounting', description: 'Reconciliation, commissions and servicing.' },
      { menuItemId: 'feedback', description: 'always open' },
      { menuItemId: 'settings', description: 'profile · templates' }
    ]
  }
] as const;

const CHILD_DESTINATION_PARENTS = new Set([
  'pipelines',
  'talent-pipeline',
  'loan-center',
  'accounting',
  'settings'
]);

const topLevelMenuItems = menuSections.flatMap((section) => section.items);

function getMenuItem(id: string): MenuItem {
  const item = topLevelMenuItems.find((candidate) => candidate.id === id);
  if (!item) {
    throw new Error(`Home navigation references missing menu item "${id}"`);
  }
  return item;
}

export function isHomeWidgetSection(
  sectionId: HomeNavigationSection['id']
): sectionId is HomeWidgetSectionId {
  return sectionId === 'workspace' || sectionId === 'production';
}

export function getWithheldMarketWatchWidget(): HomeWithheldWidget {
  const item = getMenuItem('market-watch');
  return {
    id: item.id,
    title: item.title,
    icon: item.icon
  };
}

function hasPermission(
  permissions: string[] | undefined,
  hasAnyPermission: HasAnyPermission
): boolean {
  return !permissions || permissions.length === 0 || hasAnyPermission(permissions);
}

function canOpenSection(
  sectionId: string,
  hasAnyPermission: HasAnyPermission
): boolean {
  return hasPermission(getSectionPermissions(sectionId), hasAnyPermission);
}

function resolveDestination(
  item: MenuItem,
  hasAnyPermission: HasAnyPermission
): HomeDestination | null {
  if (item.id === 'artemis') {
    return { kind: 'artemis', id: 'artemis' };
  }
  if (item.id === 'batcave') {
    return { kind: 'batcave', id: 'batcave' };
  }

  if (CHILD_DESTINATION_PARENTS.has(item.id)) {
    const child = item.subItems?.find((candidate) => (
      hasPermission(candidate.permissions, hasAnyPermission)
      && canOpenSection(candidate.id, hasAnyPermission)
    ));
    return child ? { kind: 'section', id: child.id } : null;
  }

  return canOpenSection(item.id, hasAnyPermission)
    ? { kind: 'section', id: item.id }
    : null;
}

function resolveCard(
  presentation: HomeCardPresentation,
  hasAnyPermission: HasAnyPermission
): HomeNavigationCard | null {
  const item = getMenuItem(presentation.menuItemId);
  if (!hasPermission(item.permissions, hasAnyPermission)) {
    return null;
  }

  const destination = resolveDestination(item, hasAnyPermission);
  if (!destination) {
    return null;
  }

  return {
    id: item.id,
    title: item.title,
    description: presentation.description,
    icon: item.icon,
    destination
  };
}

function resolveReports(hasAnyPermission: HasAnyPermission): HomeReportLink[] {
  const reports = getMenuItem('company-reports');
  if (!hasPermission(reports.permissions, hasAnyPermission)) {
    return [];
  }

  return (reports.subItems ?? []).flatMap((report) => {
    if (
      !hasPermission(report.permissions, hasAnyPermission)
      || !canOpenSection(report.id, hasAnyPermission)
    ) {
      return [];
    }
    return [{
      id: report.id,
      title: report.title,
      destination: { kind: 'section' as const, id: report.id }
    }];
  });
}

export function getHomeNavigationModel(
  hasAnyPermission: HasAnyPermission
): HomeNavigationModel {
  const sections = HOME_SECTION_PRESENTATION.flatMap((section) => {
    const cards = section.cards.flatMap((card) => {
      const resolved = resolveCard(card, hasAnyPermission);
      return resolved ? [resolved] : [];
    });
    return cards.length > 0 ? [{ ...section, cards }] : [];
  });

  return {
    sections,
    reports: resolveReports(hasAnyPermission)
  };
}
